import { useId, useMemo, useState, useSyncExternalStore } from 'react';
import { useMutation } from 'react-query';
import { useParams } from 'react-router-dom';
import { assetsHighlightReelGenerateCreate, type HighlightReelGenerateContentTypeEnum } from '@goldcast/api/content';
import Button from '@/components/atoms/Button/Button';
import CoEditSparkleIcon from '@/components/atoms/CoEditSparkleIcon';
import Icon from '@/components/atoms/Icon';
import AspectRatioTiles from '@/components/molecules/AspectRatioTiles/AspectRatioTiles';
import { DEFAULT_ASPECT_RATIO, type AspectRatioValue } from '@/components/molecules/AspectRatioTiles/constants';
import PopoverFooter from '@/components/molecules/PopoverCard/PopoverFooter';
import { classnames } from '@/libs/utils';
import { showErrorToast, showSuccessToast } from '@/libs/toast/toast';
import { useAppContext } from '@/context/AppContext/AppContext';
import { FeatureFlagKeys } from '@/services/featureFlag';
import featureFlagStore from '@/stores/featureFlagStore';

const TARGET_TIME_RANGES = [30, 60, 90] as const;
const DEFAULT_TARGET_TIME_RANGE = 60;

// The backend's HighlightReelGenerate.content_type accepts exactly these two choices.
const CONTENT_TYPE_OPTIONS: { value: HighlightReelGenerateContentTypeEnum; label: string }[] = [
  { value: 'keynote', label: 'Keynote' },
  { value: 'product-highlights', label: 'Product highlights' }
];
// Matches the backend default for HighlightReelGenerate.content_type.
const DEFAULT_CONTENT_TYPE: HighlightReelGenerateContentTypeEnum = 'keynote';

// The design's Label/X Small type, shared by every section heading in the panel.
const SECTION_HEADING_CLASSNAME = 'text-xs font-medium leading-[18px] text-black';

/**
 * One option in a segmented control, shared by the content type and target time range groups.
 *
 * Selected pills carry the design's white fill, 1px gray-200 border and default two-layer shadow;
 * unselected pills keep a transparent border of the same width so selecting one doesn't shift the
 * row by 2px.
 */
export function SegmentedButton({
  label,
  isSelected,
  testId,
  onClick
}: {
  label: string;
  isSelected: boolean;
  testId: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={isSelected}
      data-testid={testId}
      onClick={onClick}
      className={classnames(
        'flex-1 rounded-md border py-1.5 text-xs font-medium transition-all duration-150 ease-in-out',
        isSelected
          ? 'border-gray-200 bg-white text-slate-900 shadow'
          : 'border-transparent text-slate-500 hover:text-slate-700'
      )}
    >
      {label}
    </button>
  );
}

export default function GenerateHighlightReelPanel({
  onCancel,
  onGenerated
}: {
  onCancel: () => void;
  onGenerated: () => void;
}) {
  const { eventId, broadcastId } = useParams<{ eventId: string; broadcastId: string }>();
  const { logger } = useAppContext();
  const [duration, setDuration] = useState<number>(DEFAULT_TARGET_TIME_RANGE);
  const [aspectRatio, setAspectRatio] = useState<AspectRatioValue>(DEFAULT_ASPECT_RATIO);
  const [contentType, setContentType] = useState<HighlightReelGenerateContentTypeEnum>(DEFAULT_CONTENT_TYPE);
  const [userPrompt, setUserPrompt] = useState('');
  const userPromptId = useId();

  // Render state, so it has to re-run when the flag lands — only the subscription guarantees that.
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const isContentTypeEnabled = useMemo(
    () => !!featureFlags[FeatureFlagKeys.Use_CL_Content_Type_For_HLR],
    [featureFlags]
  );
  const isUserPromptEnabled = useMemo(() => !!featureFlags[FeatureFlagKeys.Use_CL_User_Prompt_In_HLR], [featureFlags]);
  const trimmedUserPrompt = userPrompt.trim();

  const { mutate: generateReel, isLoading: isGenerating } = useMutation(
    () =>
      assetsHighlightReelGenerateCreate({
        body: {
          content_id: broadcastId as string,
          project_id: eventId as string,
          highlight_reel_duration: duration,
          aspect_ratio: aspectRatio,
          ...(isContentTypeEnabled ? { content_type: contentType } : {}),
          ...(isUserPromptEnabled && trimmedUserPrompt ? { user_prompt: trimmedUserPrompt } : {})
        }
      }),
    {
      onSuccess: () => {
        // Generation takes minutes and users usually leave the tab, so let this toast expire in the
        // background instead of pausing until they return and seeing it stacked under the completion toast.
        showSuccessToast('Highlight reel generation has started. We’ll let you know when it’s ready.', {
          pauseOnFocusLoss: false
        });
        onGenerated();
      },
      onError: (err: any) => {
        showErrorToast('Could not start highlight reel generation. Please try again.');
        logger.error('Failed to generate highlight reel', err);
      }
    }
  );

  return (
    // PopoverCard owns the heading and close button; this fills the panel body below them.
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-y-auto px-4 pb-4 pt-5">
        <AspectRatioTiles
          label="Target platform"
          tileGapClassName="gap-2"
          labelClassName={SECTION_HEADING_CLASSNAME}
          value={aspectRatio}
          onChange={setAspectRatio}
        />
        {isContentTypeEnabled && (
          <>
            <div className={classnames('mt-4', SECTION_HEADING_CLASSNAME)}>Content type</div>
            <div
              role="radiogroup"
              aria-label="Content type"
              className="mt-2 flex w-full items-center gap-1 rounded-lg bg-slate-100 p-1"
            >
              {CONTENT_TYPE_OPTIONS.map(option => (
                <SegmentedButton
                  key={option.value}
                  label={option.label}
                  isSelected={contentType === option.value}
                  testId={`highlight-reel-content-type-${option.value}`}
                  onClick={() => setContentType(option.value)}
                />
              ))}
            </div>
          </>
        )}
        <div className={classnames('mt-4', SECTION_HEADING_CLASSNAME)}>Target time range</div>
        <div
          role="radiogroup"
          aria-label="Target time range"
          className="mt-2 flex w-full items-center gap-1 rounded-lg bg-slate-100 p-1"
        >
          {TARGET_TIME_RANGES.map(range => (
            <SegmentedButton
              key={range}
              label={`${range} s`}
              isSelected={duration === range}
              testId={`highlight-reel-duration-${range}`}
              onClick={() => setDuration(range)}
            />
          ))}
        </div>
        {isUserPromptEnabled && (
          <>
            <div id={userPromptId} className={classnames('mt-4 flex items-center gap-2', SECTION_HEADING_CLASSNAME)}>
              Prompt your idea
              <CoEditSparkleIcon className="size-4" testId="highlight-reel-user-prompt-icon" />
            </div>
            {/* Grows with its content like the co-edit composer, then scrolls past the cap. */}
            <textarea
              aria-labelledby={userPromptId}
              data-testid="highlight-reel-user-prompt"
              value={userPrompt}
              onChange={e => setUserPrompt(e.target.value)}
              placeholder="Describe what the highlight reel should focus on"
              rows={3}
              // @ts-ignore fieldSizing is not yet in React's CSSProperties
              style={{ fieldSizing: 'content' }}
              className="mt-2 block max-h-[40vh] min-h-[90px] w-full resize-none rounded-lg border border-slate-200 p-2.5 text-sm leading-5 text-slate-900 placeholder:text-slate-400 focus:border-deep-orange-600 focus:outline-none focus:ring-0"
            />
          </>
        )}
      </div>

      <PopoverFooter align="end">
        <Button
          variation="text"
          buttonSize="large"
          trackingId="generate-highlight-reel-cancel-button"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          variation="filled"
          buttonSize="large"
          trackingId="generate-highlight-reel-submit-button"
          disabled={isGenerating}
          onClick={() => generateReel()}
        >
          <Icon
            name={isGenerating ? 'IconLoader2' : 'IconSparkles'}
            size={14}
            className={classnames('mr-1.5', { 'animate-spin': isGenerating })}
          />
          Generate
        </Button>
      </PopoverFooter>
    </div>
  );
}
